import type {
  FeatureServiceBinder,
  FeatureServiceProviderDefinition,
  FeatureServices,
  Logger,
  SharedFeatureService,
} from '@feature-hub/core';
import type {LocaleV1} from './create-binder-v1.js';
import {createBinderV1} from './create-binder-v1.js';
import type {
  LocaleListenerV2,
  LocaleServiceV2,
  LocaleV2,
} from './create-binder-v2.js';
import {createBinderV2} from './create-binder-v2.js';
import type {
  LocaleListenerV3,
  LocaleServiceV3,
  LocaleV3,
} from './create-binder-v3.js';
import {createBinderV3} from './create-binder-v3.js';

export {LocaleV1};
export {LocaleListenerV2, LocaleServiceV2, LocaleV2};
export {LocaleListenerV3, LocaleServiceV3, LocaleV3};

export interface DefineLocaleServiceOptions {
  readonly locale: LocaleV3;
  readonly subscribe: (listener: LocaleListenerV3) => void;
}

export interface SharedLocaleService extends SharedFeatureService {
  readonly '1.0.0': FeatureServiceBinder<LocaleV1>;
  readonly '2.2.0': FeatureServiceBinder<LocaleServiceV2>;
  readonly '3.0.0': FeatureServiceBinder<LocaleServiceV3>;
}

export interface LocaleServiceDependencies extends FeatureServices {
  readonly 's2:logger'?: Logger;
}

export {getBaseSiteId} from './get-base-site-id.js';
export {getCountryCode} from './get-country-code.js';
export {getCurrency} from './get-currency.js';
export {getLanguage} from './get-language.js';
export {getLanguageTag} from './get-language-tag.js';

export function defineLocaleService(
  options: DefineLocaleServiceOptions,
): FeatureServiceProviderDefinition<
  SharedLocaleService,
  LocaleServiceDependencies
> {
  return {
    id: `locale-service`,

    optionalDependencies: {
      featureServices: {
        's2:logger': `^1.0.0`,
      },
    },

    create: ({featureServices}) => {
      const {'s2:logger': logger = console} = featureServices;

      return {
        '1.0.0': createBinderV1({locale: options.locale, logger}),
        '2.2.0': createBinderV2({...options, logger}),
        '3.0.0': createBinderV3(options),
      };
    },
  };
}
